/**
 * Canonical order status model — single source of truth for EVERY order view
 * (internal, o2o, marketplace, third-party) so the status label + severity + tab grouping are
 * identical across flows. Backend exposes this as the `orderStatus` field (2-field canonical model:
 * orderStatus = business 10-state, fulfillmentStatus = the operational machine).
 */
export type OrderBusinessStatus =
  | 'NEW'
  | 'CONFIRMED'
  | 'PROCESSING'
  | 'READY_TO_SHIP'
  | 'SHIPPING'
  | 'DELIVERED'
  | 'COMPLETED'
  | 'CANCELLED'
  | 'RETURN_IN_PROGRESS'
  | 'RETURNED';

/** PrimeNG tag severities. */
export type OrderStatusSeverity =
  | 'info'
  | 'warn'
  | 'success'
  | 'danger'
  | 'secondary';

/** Tab groups: the status filter tabs shared by every order list. */
export type OrderStatusGroup =
  | 'unfinished'
  | 'completed'
  | 'closed'
  | 'abnormal';

interface OrderStatusMeta {
  /** i18n key under the shared `order.status.*` namespace. */
  i18nKey: string;
  severity: OrderStatusSeverity;
  group: OrderStatusGroup;
}

export const ORDER_BUSINESS_STATUSES: OrderBusinessStatus[] = [
  'NEW',
  'CONFIRMED',
  'PROCESSING',
  'READY_TO_SHIP',
  'SHIPPING',
  'DELIVERED',
  'COMPLETED',
  'CANCELLED',
  'RETURN_IN_PROGRESS',
  'RETURNED',
];

export const ORDER_BUSINESS_STATUS_META: Record<OrderBusinessStatus, OrderStatusMeta> = {
  NEW: { i18nKey: 'order.status.NEW', severity: 'info', group: 'unfinished' },
  CONFIRMED: { i18nKey: 'order.status.CONFIRMED', severity: 'info', group: 'unfinished' },
  PROCESSING: { i18nKey: 'order.status.PROCESSING', severity: 'warn', group: 'unfinished' },
  READY_TO_SHIP: { i18nKey: 'order.status.READY_TO_SHIP', severity: 'warn', group: 'unfinished' },
  SHIPPING: { i18nKey: 'order.status.SHIPPING', severity: 'warn', group: 'unfinished' },
  DELIVERED: { i18nKey: 'order.status.DELIVERED', severity: 'success', group: 'completed' },
  COMPLETED: { i18nKey: 'order.status.COMPLETED', severity: 'success', group: 'completed' },
  CANCELLED: { i18nKey: 'order.status.CANCELLED', severity: 'secondary', group: 'closed' },
  RETURN_IN_PROGRESS: {
    i18nKey: 'order.status.RETURN_IN_PROGRESS',
    severity: 'danger',
    group: 'abnormal',
  },
  RETURNED: { i18nKey: 'order.status.RETURNED', severity: 'secondary', group: 'closed' },
};

/** The business states each shared status tab selects (sent to the backend as `orderStatusList`). */
export const ORDER_STATUS_GROUP_STATES: Record<OrderStatusGroup, OrderBusinessStatus[]> = {
  unfinished: ['NEW', 'CONFIRMED', 'PROCESSING', 'READY_TO_SHIP', 'SHIPPING'],
  completed: ['DELIVERED', 'COMPLETED'],
  closed: ['CANCELLED', 'RETURNED'],
  abnormal: ['RETURN_IN_PROGRESS'],
};

function metaOf(value: string | null | undefined): OrderStatusMeta | undefined {
  if (!value) {
    return undefined;
  }
  return ORDER_BUSINESS_STATUS_META[value as OrderBusinessStatus];
}

/** i18n key for a business order status; falls back to the raw value so nothing renders blank. */
export function getOrderStatusLabel(value: string | null | undefined): string {
  return metaOf(value)?.i18nKey ?? (value ? String(value) : '');
}

/** PrimeNG tag severity for a business order status. */
export function getOrderStatusSeverity(value: string | null | undefined): OrderStatusSeverity {
  return metaOf(value)?.severity ?? 'secondary';
}

/** business state set for a status tab ('all' → undefined = no filter, sent as `orderStatusList`). */
export function orderStatusListForTab(
  tab: 'all' | OrderStatusGroup | string | null | undefined,
): OrderBusinessStatus[] | undefined {
  if (!tab || tab === 'all') {
    return undefined;
  }
  return ORDER_STATUS_GROUP_STATES[tab as OrderStatusGroup];
}

export const ORDER_FULFILLMENT_STATUS_LABEL_BY_CODE: Record<string, string> = {
  '0': 'o2o.internal.order_status_labels.not_started',
  '30000': 'o2o.internal.order_status_labels.init_normal',
  '30101': 'o2o.internal.order_status_labels.preemption_wait',
  '30111': 'o2o.internal.order_status_labels.preemption_write_wait',
  '30112': 'o2o.internal.order_status_labels.preemption_write_success',
  '30113': 'o2o.internal.order_status_labels.preemption_write_fail',
  '30211': 'o2o.internal.order_status_labels.preemption_audit_one_wait',
  '30212': 'o2o.internal.order_status_labels.preemption_audit_one_success',
  '30213': 'o2o.internal.order_status_labels.preemption_audit_one_fail',
  '30301': 'o2o.internal.order_status_labels.sourcing_wait',
  '30302': 'o2o.internal.order_status_labels.sourcing_success',
  '30303': 'o2o.internal.order_status_labels.sourcing_fail',
  '30304': 'o2o.internal.order_status_labels.sourcing_part_success',
  '30305': 'o2o.internal.order_status_labels.sourcing_cancel',
  '30510': 'o2o.internal.order_status_labels.send_init',
  '30511': 'o2o.internal.order_status_labels.send_wait',
  '30512': 'o2o.internal.order_status_labels.send_success',
  '30513': 'o2o.internal.order_status_labels.send_fail',
  '30514': 'o2o.internal.order_status_labels.send_part_success',
  '30516': 'o2o.internal.order_status_labels.send_picked',
  '30517': 'o2o.internal.order_status_labels.send_ready_for_pickup',
  '30521': 'o2o.internal.order_status_labels.receive_wait',
  '30522': 'o2o.internal.order_status_labels.receive_success',
  '30523': 'o2o.internal.order_status_labels.part_receive_success',
  '30531': 'o2o.internal.order_status_labels.send_write_wait',
  '30532': 'o2o.internal.order_status_labels.send_write_success',
  '30533': 'o2o.internal.order_status_labels.send_write_fail',
  '31000': 'o2o.internal.order_status_labels.complete',
  '31010': 'o2o.internal.order_status_labels.cancel',
  '31120': 'o2o.internal.order_status_labels.manual_intervention',
};

const SUCCESS_CODES = new Set([
  '30112',
  '30212',
  '30302',
  '30512',
  '30522',
  '30532',
  '31000',
]);

const FAIL_CODES = new Set([
  '30113',
  '30213',
  '30303',
  '30513',
  '30533',
]);

const WARN_CODES = new Set([
  '30101',
  '30111',
  '30211',
  '30301',
  '30304',
  '30511',
  '30514',
  '30516',
  '30517',
  '30521',
  '30523',
  '30531',
  '31120',
]);

import { ORDER_INTERNAL_DELIVER_STATUS_LIST } from '@app/shared/utils/constants.util';

export function getFulfillmentStatusLabel(value: string | null | undefined): string {
  const code = String(value ?? '');
  if (!code) return '';
  if (ORDER_FULFILLMENT_STATUS_LABEL_BY_CODE[code]) {
    return ORDER_FULFILLMENT_STATUS_LABEL_BY_CODE[code];
  }
  const deliverStatus = ORDER_INTERNAL_DELIVER_STATUS_LIST.find((s) => s.value === code);
  if (deliverStatus) {
    return deliverStatus.label;
  }
  const businessLabel = getOrderStatusLabel(code);
  if (businessLabel && businessLabel.startsWith('order.status.')) {
    return businessLabel;
  }
  return code;
}

export function getFulfillmentStatusSeverity(value: string | null | undefined): string {
  const code = String(value ?? '');
  if (!code) return 'secondary';
  if (FAIL_CODES.has(code) || code === '10514' || code === '30513') {
    return 'danger';
  }
  if (code === '31010' || code === '30305' || code === '60510' || code === '70510' || code === '80510') {
    return 'secondary';
  }
  if (SUCCESS_CODES.has(code) || code === '10513' || code === '30512') {
    return 'success';
  }
  if (WARN_CODES.has(code) || code === '40510' || code === '10511' || code === '10512' || code === '30511') {
    return 'warn';
  }
  if (
    code === '0' ||
    code === '30000' ||
    code === '30510' ||
    code === '20510' ||
    code === '10510'
  ) {
    return 'info';
  }
  return getOrderStatusSeverity(code);
}
